import React, { useState } from "react";
import { api, downloadPost } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { stamp } from "./Pass";
import { hhmmOf, STEP_LABEL, type VisitDetail } from "./Visits";
import { appNow } from "./clock";
import { easternToUtcMs } from "../server/station/windows";

/**
 * Escalation (build plan §10.1 #3; D2 #30, #11): the ordered referral steps —
 * campus first responders, 911 (then campus security), local EMS, the remote
 * provider, urgent care, the ER — each with its time, the contact from
 * Config ("number not set" when blank) and the outcome; emergency transport
 * (where, who went, return time); when the Director was notified (required
 * before a visit with 911 or a transport closes); the parent contact on the
 * phone tier; and the ER packet.
 */

const nowHHMM = () => hhmmOf(new Date(appNow()).toISOString());

export function VisitEscalation({ d, onSaved }: { d: VisitDetail; onSaved: (d: VisitDetail) => void }): React.ReactElement | null {
  const v = d.visit;
  const latest = d.latestVisitKey === v.visitKey;
  const [step, setStep] = useState("");
  const [time, setTime] = useState(nowHHMM());
  const [contact, setContact] = useState("");
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [transport, setTransport] = useState({ to: v.transportedTo, by: v.accompaniedBy, back: hhmmOf(v.returnedAtUtc) });
  const [director, setDirector] = useState(hhmmOf(v.directorNotifiedUtc));
  const [call, setCall] = useState({ number: "", toName: "", time: nowHHMM(), outcome: "" });
  const [dismissal, setDismissal] = useState({ open: false, releasedBy: "", releasedTo: "", time: nowHHMM(), director: hhmmOf(v.directorNotifiedUtc), reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(Date.parse(v.startedAtUtc) || appNow()));
  const at = (hhmm: string) => (hhmm ? new Date(easternToUtcMs(day, hhmm)).toISOString() : "");
  const hasSteps = v.referralSteps.length > 0;
  const stepRules = useProblems([
    ["esc-step", !step, "Choose the step."],
    ["esc-out", !outcome.trim(), "Write the outcome."],
    ["esc-reason", hasSteps && !reason.trim(), "Write why the recorded visit is being changed."],
  ]);
  const dismissRules = useProblems([
    ["dm-by", !dismissal.releasedBy.trim(), "Write who released the student."],
    ["dm-to", !dismissal.releasedTo.trim(), "Write who the student was released to (name and relationship)."],
    ["dm-dir", !dismissal.director, "Record when the Director was notified: a medical dismissal notifies the Director."],
    ["dm-reason", Boolean(v.disposition) && v.disposition !== "Dismissed" && !dismissal.reason.trim(), "Write why the recorded visit is being changed."],
  ]);
  const callRules = useProblems([
    ["pc-num", call.number.replace(/\D/g, "").length < 7, "Enter the number dialled."],
    ["pc-out", !call.outcome, "Choose the outcome: reached, no answer, or left a message."],
  ]);
  if (!d.options.canEdit && !d.canErPacket) return null;
  const phoneTier = d.notifications.find((n) => n.method === "Phone" && n.tier !== "CallMeFirst");

  async function put(body: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    const r = await api<{ detail: VisitDetail }>("PUT", `/api/visits/${encodeURIComponent(v.visitKey)}`, body);
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(r.data.detail);
  }
  async function addStep() {
    if (!stepRules.check()) return;
    const known = d.escalation.find((x) => x.step === step);
    await put({ referralSteps: [...v.referralSteps.map((x) => ({ ...x, at: x.atUtc })), { step, at: at(time), contact: contact || known?.contact || "", outcome }], ...(hasSteps ? { correctionReason: reason } : {}) });
    setStep("");
    setOutcome("");
    setReason("");
  }
  async function logCall() {
    if (!callRules.check()) return;
    setBusy(true);
    setError(null);
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/parent-contact`, { number: call.number, toName: call.toName, at: at(call.time), outcome: call.outcome });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not logged.");
    onSaved(r.data.detail);
  }
  async function dismiss() {
    if (!dismissRules.check()) return;
    setBusy(true);
    setError(null);
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/dismiss`, { releasedBy: dismissal.releasedBy, releasedTo: dismissal.releasedTo, at: at(dismissal.time), directorNotifiedAt: at(dismissal.director), correctionReason: dismissal.reason });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not recorded.");
    onSaved(r.data.detail);
  }
  async function packet() {
    setError(null);
    const e = await downloadPost(`/api/visits/${encodeURIComponent(v.visitKey)}/er-packet`, `${d.person.personKey}-er-packet.pdf`);
    if (e) setError(e);
  }

  return (
    <section className="section-card space-y-3 border-l-4 border-l-red-600">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold">Escalation and transport</h3>
        {d.canErPacket && <button type="button" className="btn-secondary min-h-11" onClick={() => void packet()}>ER packet (PDF)</button>}
      </div>
      {hasSteps ? (
        <ol className="list-decimal space-y-1 pl-5 text-sm">
          {v.referralSteps.map((x, i) => <li key={i}><span className="font-medium">{STEP_LABEL[x.step] ?? x.step}</span> · {stamp(x.atUtc)} · {x.contact || "—"} · {x.outcome}</li>)}
        </ol>
      ) : (
        <p className="text-sm text-slate-600">No escalation steps.</p>
      )}
      {latest && d.options.canEdit && (
        <div className="rounded-md border border-slate-200 p-3">
          <p className="text-sm font-semibold">Add a step</p>
          <div className="mt-1 grid gap-2 sm:grid-cols-2">
            <div>
              <label htmlFor="esc-step" className="field-label">Step</label>
              <select id="esc-step" className="field-input min-h-11" {...invalid("esc-step", stepRules.problems)} value={step} onChange={(e) => { setStep(e.target.value); setContact(d.escalation.find((x) => x.step === e.target.value)?.contact ?? ""); }}>
                <option value="">Choose…</option>
                {d.escalation.map((x) => <option key={x.step} value={x.step}>{x.label}</option>)}
              </select>
              <FieldProblem id="esc-step" problems={stepRules.problems} />
              {step && !d.escalation.find((x) => x.step === step)?.set && <p className="text-xs font-medium text-amber-900">Number not set (Config: Contacts).</p>}
            </div>
            <div><label htmlFor="esc-t" className="field-label">Time</label><input id="esc-t" type="time" className="field-input" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            <div><label htmlFor="esc-c" className="field-label">Who was contacted</label><input id="esc-c" className="field-input" value={contact} onChange={(e) => setContact(e.target.value)} /></div>
            <div><label htmlFor="esc-out" className="field-label">Outcome</label><input id="esc-out" className="field-input" {...invalid("esc-out", stepRules.problems)} value={outcome} onChange={(e) => setOutcome(e.target.value)} /><FieldProblem id="esc-out" problems={stepRules.problems} /></div>
          </div>
          {hasSteps && (
            <div className="mt-2">
              <label htmlFor="esc-reason" className="field-label">The steps are recorded: a new step saves a new version. Why? <span className="text-red-600">*</span></label>
              <input id="esc-reason" className="field-input" placeholder="e.g. called 911 after the responders arrived" {...invalid("esc-reason", stepRules.problems)} value={reason} onChange={(e) => setReason(e.target.value)} />
              <FieldProblem id="esc-reason" problems={stepRules.problems} />
            </div>
          )}
          <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void addStep()}>Add the step</button>
        </div>
      )}
      {latest && d.options.canEdit && (
        <div className="rounded-md border border-slate-200 p-3">
          <p className="text-sm font-semibold">Emergency transport</p>
          <div className="mt-1 grid gap-2 sm:grid-cols-3">
            <div><label htmlFor="tr-to" className="field-label">Taken to</label><input id="tr-to" className="field-input" value={transport.to} onChange={(e) => setTransport({ ...transport, to: e.target.value })} /></div>
            <div><label htmlFor="tr-by" className="field-label">Accompanied by</label><input id="tr-by" className="field-input" value={transport.by} onChange={(e) => setTransport({ ...transport, by: e.target.value })} /></div>
            <div><label htmlFor="tr-back" className="field-label">Returned at</label><input id="tr-back" type="time" className="field-input" value={transport.back} onChange={(e) => setTransport({ ...transport, back: e.target.value })} /></div>
          </div>
          <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void put({ transportedTo: transport.to, accompaniedBy: transport.by, returnedAt: at(transport.back) })}>Save the transport</button>
          <p className="mt-1 text-xs text-slate-500">A recorded destination or companion changes only as a new version (Save on the visit form, with a reason).</p>
        </div>
      )}
      {latest && d.options.canEdit && (
        <div className={`rounded-md border p-3 ${d.directorRequired && !v.directorNotifiedUtc ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}>
          <p className="text-sm font-semibold">Director notified{d.directorRequired && <span className="text-red-600"> * (911 or a transport: required before the visit closes)</span>}</p>
          <div className="mt-1 flex flex-wrap items-end gap-2">
            <div><label htmlFor="dir-t" className="field-label">At</label><input id="dir-t" type="time" className="field-input" value={director} onChange={(e) => setDirector(e.target.value)} /></div>
            <button type="button" className="btn-secondary min-h-11" disabled={busy || !director} onClick={() => void put({ directorNotifiedAt: at(director) })}>Save</button>
            {v.directorNotifiedUtc && <span className="text-sm text-emerald-800">Notified {stamp(v.directorNotifiedUtc)}</span>}
          </div>
        </div>
      )}
      {latest && d.options.canEdit && phoneTier && (
        <div className="rounded-md border border-slate-200 p-3">
          <p className="text-sm font-semibold">Parent contact (phone){phoneTier.status === "Reached" ? <span className="ml-2 font-normal text-emerald-800">reached</span> : <span className="ml-2 font-normal text-amber-900">notification incomplete until a parent is reached</span>}</p>
          <div className="mt-1 grid gap-2 sm:grid-cols-4">
            <div><label htmlFor="pc-num" className="field-label">Number dialled</label><input id="pc-num" type="tel" className="field-input" {...invalid("pc-num", callRules.problems)} value={call.number} onChange={(e) => setCall({ ...call, number: e.target.value })} /><FieldProblem id="pc-num" problems={callRules.problems} /></div>
            <div><label htmlFor="pc-to" className="field-label">Spoke to (or tried)</label><input id="pc-to" className="field-input" value={call.toName} onChange={(e) => setCall({ ...call, toName: e.target.value })} /></div>
            <div><label htmlFor="pc-t" className="field-label">Time</label><input id="pc-t" type="time" className="field-input" value={call.time} onChange={(e) => setCall({ ...call, time: e.target.value })} /></div>
            <div>
              <label htmlFor="pc-out" className="field-label">Outcome</label>
              <select id="pc-out" className="field-input" {...invalid("pc-out", callRules.problems)} value={call.outcome} onChange={(e) => setCall({ ...call, outcome: e.target.value })}>
                <option value="">Choose…</option>
                <option value="Reached">Reached</option>
                <option value="NoAnswer">No answer</option>
                <option value="LeftMessage">Left a message</option>
              </select>
              <FieldProblem id="pc-out" problems={callRules.problems} />
            </div>
          </div>
          <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void logCall()}>Log the call</button>
        </div>
      )}
      {latest && d.options.canEdit && d.person.personType === "Student" && (
        <div className="rounded-md border border-red-300 p-3">
          {d.person.departed ? (
            <p className="text-sm font-semibold text-red-800">This student has left the session (medical dismissal or departure). Their schedule has stopped; medications are to be returned.</p>
          ) : !dismissal.open ? (
            <button type="button" className="btn-secondary min-h-11" onClick={() => setDismissal({ ...dismissal, open: true })}>Medical dismissal…</button>
          ) : (
            <>
              <p className="text-sm font-semibold">Medical dismissal</p>
              <p className="text-xs text-slate-600">The student leaves the session: the medication schedule stops (later doses are departed, not missed) and their medications are to be returned. The Director must be notified.</p>
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                <div><label htmlFor="dm-by" className="field-label">Released by</label><input id="dm-by" className="field-input" {...invalid("dm-by", dismissRules.problems)} value={dismissal.releasedBy} onChange={(e) => setDismissal({ ...dismissal, releasedBy: e.target.value })} /><FieldProblem id="dm-by" problems={dismissRules.problems} /></div>
                <div><label htmlFor="dm-to" className="field-label">Released to (name, relationship)</label><input id="dm-to" className="field-input" {...invalid("dm-to", dismissRules.problems)} value={dismissal.releasedTo} onChange={(e) => setDismissal({ ...dismissal, releasedTo: e.target.value })} /><FieldProblem id="dm-to" problems={dismissRules.problems} /></div>
                <div><label htmlFor="dm-t" className="field-label">Time of release</label><input id="dm-t" type="time" className="field-input" value={dismissal.time} onChange={(e) => setDismissal({ ...dismissal, time: e.target.value })} /></div>
                <div><label htmlFor="dm-dir" className="field-label">Director notified at</label><input id="dm-dir" type="time" className="field-input" {...invalid("dm-dir", dismissRules.problems)} value={dismissal.director} onChange={(e) => setDismissal({ ...dismissal, director: e.target.value })} /><FieldProblem id="dm-dir" problems={dismissRules.problems} /></div>
              </div>
              {v.disposition && v.disposition !== "Dismissed" && (
                <div className="mt-2"><label htmlFor="dm-reason" className="field-label">The disposition is recorded: this saves a new version. Why? <span className="text-red-600">*</span></label><input id="dm-reason" className="field-input" {...invalid("dm-reason", dismissRules.problems)} value={dismissal.reason} onChange={(e) => setDismissal({ ...dismissal, reason: e.target.value })} /><FieldProblem id="dm-reason" problems={dismissRules.problems} /></div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void dismiss()}>Record the medical dismissal</button>
                <button type="button" className="btn-secondary min-h-11" onClick={() => setDismissal({ ...dismissal, open: false })}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
      {error && <p className="field-error" role="alert">{error}</p>}
    </section>
  );
}
